import { ReactNode, useEffect, useRef } from 'react';
import { MaintenanceHeader } from './MaintenanceHeader';

/**
 * Full-screen takeover shell for all three maintenance states.
 * `fixed inset-0 z-takeover` blocks all underlying interaction. The token
 * (src/styles/z-scale.css) sits above the toast layer - the Sonner Toaster is
 * pinned to --z-index-toast in ui/sonner.tsx - and above every overlay
 * primitive, so the takeover is never painted-over during a real outage.
 * (Sonner's own stylesheet default is z-index 999999999; the old hardcoded
 * z-[10000] never actually outranked it.)
 * pt-safe + pb-safe respect device safe-area insets (iOS notch).
 *
 * Two-level scroll structure for the content area:
 * - Outer wrapper (`flex-1 overflow-y-auto`) is the scroll container.
 * - Inner wrapper (`min-h-full flex items-center justify-center p-6`)
 *   centers content when it fits, and grows past the parent height when
 *   it doesn't (because `min-h-full` is a minimum, not a maximum).
 *
 * Without this, on a small viewport (iPhone SE landscape ~190px after
 * safe-area + header) or with iOS dynamic type at the largest
 * accessibility setting, content overflows the centered flex and the
 * Try Again button becomes unreachable - the user is locked out of the
 * only available action during a real outage.
 *
 * Owns the Try Again button so all three states share identical focus
 * management and styling. role="status" + aria-live="polite" announces
 * the takeover to assistive tech without interrupting the user.
 */
interface MaintenanceLayoutProps {
  children: ReactNode;
  onTryAgain: () => void;
}

export function MaintenanceLayout({ children, onTryAgain }: MaintenanceLayoutProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Capture whatever owned focus before the takeover stole it, so we can
    // restore focus on unmount. Without this, dismissing the takeover after
    // a long outage lands focus on document.body and a keyboard / SR user
    // has to re-navigate from the top of the page.
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    buttonRef.current?.focus();
    return () => {
      // The previous focus target may have unmounted while the takeover
      // was up (e.g. the user navigated away on a stale tab). Calling
      // focus on a detached element is a silent no-op, so no guard needed.
      previouslyFocusedRef.current?.focus?.();
    };
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-takeover bg-mb-bg flex flex-col pt-safe pb-safe"
    >
      <MaintenanceHeader />
      <div className="flex-1 overflow-y-auto">
        <div className="min-h-full flex items-center justify-center p-6">
          <div className="w-full max-w-md text-center space-y-4">
            {children}
            <button
              ref={buttonRef}
              type="button"
              onClick={onTryAgain}
              className="w-full max-w-xs mx-auto rounded-full bg-mb-teal py-2.5 text-mb-ink font-medium hover:bg-mb-teal-dark transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mb-teal-ink focus-visible:ring-offset-2"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
